import React, { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link } from '~/lib/react-router-dom';

import { Alert, Avatar, Box, Chip, Link as MuiLink, Paper, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridToolbar } from '@mui/x-data-grid';

import { LUXURY_ESCAPES } from '~/consts/brands';

import { useGetAllCustomerServiceRepAssignments } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';

import { formatDateDDMonYY } from '~/services/TimeService';

import { matchesCurrentInfiniteUserSearch } from './currentInfiniteUserSearch';
import { INFINITE_MEMBER_ROLE_LABELS, InfiniteMemberRole, loadInfiniteUserContacts } from './loadInfiniteUserContacts';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const SEARCH_LABEL = 'Search name, email or phone';

type CurrentInfiniteUserRow = {
  id: string;
  customerId: string;
  accountNumber: number;
  fullName: string;
  email: string;
  phone: string;
  phonePrefix: string;
  role: InfiniteMemberRole | null;
  enrolledAt: string | null;
  representativeName: string;
  region: string;
  updatedAt: string;
};

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

const GRID_SX = {
  border: 0,
  '& .MuiDataGrid-columnHeaderTitle': {
    color: 'text.secondary',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-cell': {
    alignItems: 'center',
    display: 'flex',
  },
} as const;

/** Read-only list of members currently enrolled on Infinite. */
export default function CurrentInfiniteUsersTab() {
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const assignmentsQuery = useGetAllCustomerServiceRepAssignments();
  const accounts = useMemo(() => assignmentsQuery.data?.accounts ?? [], [assignmentsQuery.data?.accounts]);
  const customerIds = useMemo(() => [...new Set(accounts.map((account) => account.customerId))], [accounts]);

  const summariesQuery = useGetUsersSummaryByIds({
    userIds: customerIds,
    brand: LUXURY_ESCAPES,
    enabled: customerIds.length > 0,
  });
  const contactsQuery = useQuery({
    queryKey: ['lux-loyalty', 'current-infinite-user-contacts', customerIds],
    queryFn: () => loadInfiniteUserContacts(customerIds),
    enabled: customerIds.length > 0,
    staleTime: 60_000,
  });

  const rows = useMemo<Array<CurrentInfiniteUserRow>>(() => {
    return accounts.map((account) => {
      const summary = summariesQuery.data?.get(account.customerId);
      const contact = contactsQuery.data?.get(account.customerId);
      const fullName = summary?.full_name || contact?.fullName || '';
      const email = summary?.email || contact?.email || '';
      const phonePrefix = contact?.phonePrefix ?? '';
      const phone = contact?.phone ?? '';
      const representative = account.customerServiceRep;

      return {
        id: account.id,
        customerId: account.customerId,
        accountNumber: account.accountNumber,
        fullName,
        email,
        phone,
        phonePrefix,
        role: contact?.role ?? null,
        enrolledAt: contact?.enrolledAt ?? null,
        representativeName: representative
          ? `${representative.firstName} ${representative.lastName}`.trim()
          : 'Unassigned',
        region: account.region,
        updatedAt: account.updatedAt,
      };
    });
  }, [accounts, contactsQuery.data, summariesQuery.data]);

  const visibleRows = useMemo(
    () => rows.filter((row) => matchesCurrentInfiniteUserSearch(row, search)),
    [rows, search],
  );

  const searchLooksLikePhone = digitsInSearch(search) >= 3;
  const isLoading =
    assignmentsQuery.isLoading ||
    (customerIds.length > 0 && summariesQuery.isLoading && !summariesQuery.data) ||
    (searchLooksLikePhone && contactsQuery.isLoading && !contactsQuery.data);

  const columns = useMemo<Array<GridColDef<CurrentInfiniteUserRow>>>(
    () => [
      {
        field: 'fullName',
        headerName: 'Member',
        minWidth: 260,
        flex: 1.3,
        valueGetter: (_value, row) => [row.fullName, row.email].filter(Boolean).join(' '),
        renderCell: ({ row }: GridRenderCellParams<CurrentInfiniteUserRow>) => (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.900', fontSize: 14 }}>
              {initialsFromName(row.fullName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <MuiLink
                component={Link}
                to={`/users/${row.customerId}`}
                variant="body1"
                underline="hover"
                color="text.primary"
                noWrap
                title={row.fullName || row.customerId}
                sx={{ fontWeight: 700, display: 'block' }}
              >
                {row.fullName || 'Unknown member'}
              </MuiLink>
              <Typography variant="body2" color="text.secondary" noWrap title={row.email}>
                {row.email || '—'}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        field: 'role',
        headerName: 'Role',
        minWidth: 130,
        flex: 0.4,
        valueGetter: (_value, row) => (row.role ? INFINITE_MEMBER_ROLE_LABELS[row.role] : ''),
        renderCell: ({ row }: GridRenderCellParams<CurrentInfiniteUserRow>) =>
          row.role ? (
            <Chip size="small" label={INFINITE_MEMBER_ROLE_LABELS[row.role]} />
          ) : (
            <Typography color="text.secondary">{contactsQuery.isLoading ? '…' : '—'}</Typography>
          ),
      },
      {
        field: 'accountNumber',
        headerName: 'Account number',
        minWidth: 150,
        flex: 0.5,
      },
      {
        field: 'region',
        headerName: 'Region',
        minWidth: 90,
        flex: 0.3,
      },
      {
        field: 'representativeName',
        headerName: 'Representative',
        minWidth: 200,
        flex: 0.8,
      },
      {
        field: 'enrolledAt',
        headerName: 'Enrolled',
        minWidth: 140,
        flex: 0.5,
        type: 'dateTime',
        valueGetter: (_value, row) => (row.enrolledAt ? new Date(row.enrolledAt) : null),
        valueFormatter: (value) => (value ? formatDateDDMonYY(value) : ''),
        renderCell: ({ row }: GridRenderCellParams<CurrentInfiniteUserRow>) => (
          <Typography noWrap>{enrolmentLabel(row.enrolledAt, contactsQuery.isLoading)}</Typography>
        ),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        minWidth: 130,
        flex: 0.45,
        type: 'dateTime',
        valueGetter: (_value, row) => (row.updatedAt ? new Date(row.updatedAt) : null),
        valueFormatter: (value) => (value ? formatDateDDMonYY(value) : ''),
      },
    ],
    [contactsQuery.isLoading],
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Current Users
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 1200 }}>
        Members currently enrolled on Infinite. Search to find someone. This list is view only.
      </Typography>

      <TextField
        label={SEARCH_LABEL}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPaginationModel((current) => ({ ...current, page: 0 }));
        }}
        sx={{ mb: 2, minWidth: { md: 360 } }}
      />

      <Stack direction="row" justifyContent="flex-end" mb={1}>
        <Typography variant="caption" color="text.secondary">
          {visibleRows.length} member{visibleRows.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {assignmentsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load current Infinite users.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={isLoading}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          slots={{ toolbar: GridToolbar }}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={92}
          localeText={{
            noRowsLabel: search.trim() ? 'No Infinite members match this search.' : 'No current Infinite members.',
          }}
          sx={GRID_SX}
        />
      </Paper>
    </Box>
  );
}

function digitsInSearch(query: string): number {
  return query.replace(/\D/g, '').length;
}

function enrolmentLabel(enrolledAt: string | null, isLoading: boolean): string {
  if (enrolledAt) {
    return formatDateDDMonYY(enrolledAt);
  }
  return isLoading ? '…' : '—';
}
